import { Footer } from '../components/Footer';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, CheckCircle2, Link2Off } from 'lucide-react';
import { toast } from 'sonner';
import { trackView, trackConversion } from '@/lib/analytics';
import { API } from '@/lib/helpers';

const FONT_MAP = {
  'Inter': "'Inter', sans-serif",
  'Georgia': "'Georgia', serif",
  'Playfair Display': "'Playfair Display', serif",
  'Roboto Mono': "'Roboto Mono', monospace",
  'Poppins': "'Poppins', sans-serif",
};

const BG_PRESETS = {
  lavender_mist: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0e7fe 100%)',
  rose_quartz: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)',
  morning_sky: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
  sage_garden: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf9 100%)',
  warm_sand: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)',
  pearl: 'linear-gradient(180deg, #fafafa 0%, #f5f5f4 50%, #fafaf9 100%)',
  soft_lilac: 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 60%, #faf5ff 100%)',
  ocean_breeze: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 50%, #eff6ff 100%)',
  blush_peach: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fef2f2 100%)',
  mint_cream: 'linear-gradient(180deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)',
  twilight: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)',
  midnight: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
};

function getBackgroundStyle(page) {
  if (!page) return { background: 'linear-gradient(to bottom right, #f5f3ff, #eef2ff)' };
  const bgType = page.bg_type || 'solid';
  if (bgType === 'gradient') {
    const dir = page.bg_gradient_direction || 'to bottom';
    return { background: `linear-gradient(${dir}, ${page.cor_fundo || '#f8fafc'}, ${page.cor_fundo_2 || '#e2e8f0'})` };
  }
  if (bgType === 'preset' && page.bg_preset && BG_PRESETS[page.bg_preset]) {
    return { background: BG_PRESETS[page.bg_preset] };
  }
  return { backgroundColor: page.cor_fundo || '#f8fafc' };
}

export default function PublicPerguntas() {
  const { slug } = useParams();
  const [pergunta, setPergunta] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [settings, setSettings] = useState({ app_name: '' });
  const [pageStyle, setPageStyle] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [invalidSlug, setInvalidSlug] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      setPageLoading(true);
      try {
        if (slug) {
          const r = await fetch(`${API}/churches/${slug}/public`);
          if (!r.ok) { setInvalidSlug(true); return; }
          const data = await r.json();
          setSettings({ app_name: data.app_name });
          if (data.page_style) setPageStyle(data.page_style);
          document.title = 'CCBRJM';
          trackView('perguntas', slug, '');
        } else {
          const r = await fetch(`${API}/settings`);
          if (r.ok) setSettings(await r.json());
        }
      } catch { setInvalidSlug(true); }
      finally { setPageLoading(false); }
    };
    fetchSettings();
  }, [slug]);

  const bgStyle = getBackgroundStyle(pageStyle);
  const fontFamily = pageStyle?.fonte ? (FONT_MAP[pageStyle.fonte] || FONT_MAP['Inter']) : "'Inter', sans-serif";
  const titleColor = pageStyle?.cor_fonte_nome || undefined;
  const iconColor = pageStyle?.cor_icone || '#8b5cf6';
  const iconBg = pageStyle?.cor_fundo_icone || undefined;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pergunta.trim()) { toast.error('Escreva sua pergunta'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/public/pergunta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pergunta: pergunta.trim(), church_slug: slug || '' })
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro ao enviar'); }
      setSubmitted(true);
      toast.success('Pergunta enviada!');
      trackConversion('perguntas', slug, '');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
        <div className="text-slate-500" data-testid="pergunta-loading">Carregando...</div>
      </div>
    );
  }

  if (invalidSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center" data-testid="pergunta-invalid-slug">
          <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Link2Off className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-700 mb-1">Página não encontrada</h2>
          <p className="text-sm text-slate-500">O link que você acessou não existe ou foi desativado.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
        <Card className="w-full max-w-md p-8 bg-white/90 backdrop-blur-sm shadow-xl border-0 text-center" data-testid="pergunta-success">
          <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-green-600" /></div>
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily, color: titleColor }}>Pergunta Enviada!</h2>
          <p className="text-slate-600 mb-6">Sua pergunta foi recebida. Obrigado por participar!</p>
          <Button onClick={() => { setSubmitted(false); setPergunta(''); }} variant="outline" data-testid="new-pergunta-btn">Enviar outra pergunta</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4" style={{ ...bgStyle, fontFamily }}>
      <div className="max-w-lg w-full">
        <Card className="bg-white/90 backdrop-blur-sm shadow-xl border-0" data-testid="pergunta-form-card">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg" style={{ backgroundColor: iconBg || '#7c3aed' }}>
              <MessageSquare className="w-8 h-8" style={{ color: iconColor || '#ffffff' }} />
            </div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily, color: titleColor }}>Envio de Perguntas</CardTitle>
            <CardDescription style={{ color: pageStyle?.cor_fonte_bio || undefined }}>Use este espaço para enviar perguntas sobre a fé, vida cristã ou qualquer outro assunto. Nos comprometemos a responder com amor, respeito e sempre com base na Palavra de Deus.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Textarea value={pergunta} onChange={e => setPergunta(e.target.value)} rows={4} maxLength={9000} data-testid="textarea-pergunta" className="resize-none" />
                <p className="text-xs text-slate-400 mt-1 text-right">{pergunta.length}/9000</p>
              </div>
              <Button type="submit" className="w-full h-12 font-medium shadow-md" style={{ backgroundColor: iconBg || '#8b5cf6', color: iconColor || '#ffffff' }} disabled={loading} data-testid="submit-pergunta-btn">
                {loading ? 'Enviando...' : 'Enviar Pergunta'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Footer />
      </div>
    </div>
  );
}
